import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type KeyboardEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from '../../../icons';
import './GroupedSelect.css';

/**
 * GroupedSelect — **묶음이 있는 고르개** (2026-09-15 · 대시보드 시안 v2 V8 · 정본 제안 B4-2 `#GROUPSELECT`).
 *
 * 쓰는 곳: 근태 신청 「휴가 유형」(V8) · 「즐겨찾는 결재선」(V9 — 둘째 줄 · 발 행동 · 줄 지우기).
 *
 * ★★★ **왜 `Combobox` 를 안 늘렸나.** `Combobox` 는 묶음(option group)을 «미룬 것»으로 적어 두었고
 *     90여 곳이 그리는 자리를 공유한다. 묶음 · 오른쪽 값 · 단위 태그 · 막힘 이유를 거기 넣으면 그 전부의
 *     렌더 경로가 함께 움직인다. 같은 WAI-ARIA combobox 패턴을 **새 파일**로 따른다(작업 규칙 additive).
 * ★★ **검색칸은 선택지가 `searchThreshold`(기본 10)를 넘을 때만** 선다(사용자 결정 A-Q1).
 * ★★ **막힌 선택지는 보이되 고를 수 없다** — 이유를 함께 읽어 준다(흐리게만 하면 왜인지 모른다).
 *    키보드 이동도 막힌 줄을 건너뛴다.
 * ★★ **Esc 는 목록만 닫는다** — 공용 `Modal` 은 window 의 Escape 를 무조건 받아 창을 닫으므로 여기서 멈춘다.
 * ★ 목록은 **body 로 띄운다** — 모달 본문(`overflow-y:auto`) 안에서 잘리지 않게(`Combobox portal` 과 같은 이유).
 *   트리거 자리를 재서 아래가 모자라면 위로 연다 · 스크롤을 따라간다.
 * ★ Tab 은 닫고 자연 흐름 — 단 **발(footer)이 있으면** 발의 단추로 옮겨 가게 두고, 포커스가 목록 밖으로 나갈 때 닫는다.
 * ★ 줄 지우기(`onRemove`)는 옵션 **옆** 단추다(옵션 안에 단추를 넣지 않는다) · 키보드는 Delete.
 */

export interface GroupedSelectOption {
  value: string;
  label: string;
  /** 이름 옆 작은 태그(단위 — 「날짜 범위」 · 「0.5일」 / 배지 — 「기본 · 휴가」). */
  tag?: string;
  /** 오른쪽 값(잔여 — 「7.5일」 · 「—」). */
  meta?: string;
  /** 둘째 줄(결재선 요약 — 「박서준(검토) → 이지훈(결재)」). */
  detail?: string;
  disabled?: boolean;
  /** 막힌 까닭 — 읽어 주고, 마우스를 올리면 보인다. */
  disabledReason?: string;
  /** `onRemove` 가 있을 때 이 줄에 지우기 단추를 세운다. */
  removable?: boolean;
}

export interface GroupedSelectGroup {
  key: string;
  label: string;
  options: readonly GroupedSelectOption[];
}

export interface GroupedSelectProps {
  value: string | null;
  onChange: (value: string) => void;
  groups: readonly GroupedSelectGroup[];
  /** 트리거 · 목록의 이름(스크린리더). */
  ariaLabel: string;
  placeholder?: string;
  /** 트리거 오른쪽 작은 글자 — 고른 것의 요약(「잔여 7.5일 · 날짜 범위」). */
  summary?: string;
  /** 선택지가 이 수를 **넘으면** 검색칸이 선다. */
  searchThreshold?: number;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** 트리거 왼쪽 글자를 직접 그린다(배지를 붙일 때). 없으면 고른 이름 · placeholder. */
  renderValue?: (selected: GroupedSelectOption | null) => ReactNode;
  /** 목록 아래 행동 줄 — `close()` 를 불러 목록을 닫는다. */
  footer?: (close: () => void) => ReactNode;
  /** 줄 지우기 — `removable` 인 줄에만 단추가 선다. */
  onRemove?: (option: GroupedSelectOption) => void;
  removeLabel?: (option: GroupedSelectOption) => string;
}

export const GROUPED_SELECT_SEARCH_THRESHOLD = 10;

const EDGE = 8;
const GAP = 4;
const MIN_ROOM = 200;

export function GroupedSelect({
  value, onChange, groups, ariaLabel, placeholder = '선택', summary,
  searchThreshold = GROUPED_SELECT_SEARCH_THRESHOLD, searchPlaceholder = '찾기',
  emptyText = '찾는 항목이 없습니다', disabled = false, className, id,
  renderValue, footer, onRemove, removeLabel,
}: GroupedSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState<string | null>(null);
  const [placeStyle, setPlaceStyle] = useState<CSSProperties | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const uid = useId();
  const listId = `${uid}-list`;
  const optId = (v: string) => `${uid}-opt-${v}`;

  const total = groups.reduce((n, g) => n + g.options.length, 0);
  const searchable = total > searchThreshold;
  const q = searchable ? query.trim().toLowerCase() : '';

  const shown = useMemo(
    () => groups
      .map((g) => ({ ...g, options: q ? g.options.filter((o) => o.label.toLowerCase().includes(q)) : g.options }))
      .filter((g) => g.options.length > 0),
    [groups, q],
  );
  const enabled = useMemo(() => shown.flatMap((g) => g.options).filter((o) => !o.disabled), [shown]);
  const selected = useMemo(
    () => groups.flatMap((g) => g.options).find((o) => o.value === value) ?? null,
    [groups, value],
  );

  const close = useCallback((focusTrigger: boolean) => {
    setOpen(false);
    setQuery('');
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  /* 열 때 — 고른 것이 고를 수 있으면 거기, 아니면 첫 줄. 검색칸이 있으면 거기로 포커스. */
  useEffect(() => {
    if (!open) return;
    setActive(selected && !selected.disabled ? selected.value : (enabled[0]?.value ?? null));
    requestAnimationFrame(() => (searchable ? searchRef.current : listRef.current)?.focus());
    // 여는 순간 한 번만 — 걸러낼 때마다 포커스를 다시 옮기지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* 걸러서(또는 지워서) 지금 줄이 사라지면 남은 첫 줄로. */
  useEffect(() => {
    if (!open) return;
    setActive((cur) => (cur && enabled.some((o) => o.value === cur) ? cur : (enabled[0]?.value ?? null)));
  }, [enabled, open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !popRef.current?.contains(t)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const up = below < MIN_ROOM && above > below;
    const width = r.width > 0 ? r.width : undefined;
    const left = width === undefined ? EDGE : Math.max(EDGE, Math.min(r.left, window.innerWidth - width - EDGE));
    setPlaceStyle(up
      ? { left, width, bottom: window.innerHeight - r.top + GAP, maxHeight: Math.max(MIN_ROOM, above) }
      : { left, width, top: r.bottom + GAP, maxHeight: Math.max(MIN_ROOM, below) });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open || !active) return;
    document.getElementById(optId(active))?.scrollIntoView?.({ block: 'nearest' });
    // optId 는 uid 로만 정해진다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const pick = (o: GroupedSelectOption) => {
    if (o.disabled) return;
    onChange(o.value);
    close(true);
  };

  const step = (delta: 1 | -1) => {
    if (enabled.length === 0) return;
    const i = enabled.findIndex((o) => o.value === active);
    const next = i < 0 ? (delta > 0 ? 0 : enabled.length - 1) : (i + delta + enabled.length) % enabled.length;
    setActive(enabled[next]!.value);
  };

  const onListKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const inInput = e.currentTarget instanceof HTMLInputElement;
    switch (e.key) {
      case 'ArrowDown': e.preventDefault(); step(1); break;
      case 'ArrowUp': e.preventDefault(); step(-1); break;
      case 'Home':
        if (inInput) break;   // 검색칸에서는 글자 사이를 옮긴다
        e.preventDefault(); if (enabled[0]) setActive(enabled[0].value); break;
      case 'End':
        if (inInput) break;
        e.preventDefault(); if (enabled.length > 0) setActive(enabled[enabled.length - 1]!.value); break;
      case 'Enter': {
        e.preventDefault();
        const o = enabled.find((x) => x.value === active);
        if (o) pick(o);
        break;
      }
      case 'Delete': {
        if (inInput || !onRemove) break;
        const o = enabled.find((x) => x.value === active);
        if (o?.removable) { e.preventDefault(); onRemove(o); }
        break;
      }
      case 'Escape':
        e.preventDefault();
        /* ★★★ 목록만 닫는다 — 공용 `Modal` 은 window 의 Escape 를 무조건 받아 **창을 통째로 닫는다**
               (`useEscapeClose`). 여기서 멈추지 않으면 휴가 유형 목록을 닫으려다 근태 신청 창이 사라진다. */
        e.stopPropagation();
        close(true);
        break;
      case 'Tab':
        // 발이 있으면 그 단추로 옮겨 간다 — 닫는 것은 포커스가 목록 밖으로 나갈 때(onBlur).
        if (!footer) close(false);
        break;
      default: break;
    }
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  };

  const activeId = open && active ? optId(active) : undefined;

  const popover = (
    <div
      ref={popRef}
      className="wc-gselect__pop"
      style={placeStyle ?? undefined}
      role="presentation"
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null;
        // 누른 곳이 포커스를 못 받는 자리면(null) 닫지 않는다 — 바깥 누르기는 mousedown 이 맡는다.
        if (!next || popRef.current?.contains(next) || triggerRef.current?.contains(next)) return;
        close(false);
      }}
      onKeyDown={(e) => {
        // 발의 단추에서 누른 Esc 도 목록만 닫는다.
        if (e.key === 'Escape' && !(e.target instanceof HTMLInputElement) && e.target !== listRef.current) {
          e.preventDefault();
          e.stopPropagation();
          close(true);
        }
      }}
    >
      {searchable && (
        <input
          ref={searchRef}
          type="search"
          className="wc-gselect__search"
          value={query}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          aria-controls={listId}
          aria-activedescendant={activeId}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onListKeyDown}
        />
      )}
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={ariaLabel}
        className="wc-gselect__list"
        tabIndex={searchable ? -1 : 0}
        aria-activedescendant={searchable ? undefined : activeId}
        onKeyDown={searchable ? undefined : onListKeyDown}
      >
        {shown.length === 0 && <div className="wc-gselect__empty">{emptyText}</div>}
        {shown.map((g) => {
          const gid = `${uid}-g-${g.key}`;
          return (
            <div key={g.key} role="group" aria-labelledby={gid} className="wc-gselect__group">
              <div id={gid} className="wc-gselect__cap">{g.label}</div>
              {g.options.map((o) => {
                const name = [o.label, o.tag, o.meta, o.detail, o.disabled ? o.disabledReason : null].filter(Boolean).join(', ');
                const cls = [
                  'wc-gselect__opt',
                  o.detail ? 'has-detail' : '',
                  o.value === value ? 'is-selected' : '',
                  o.value === active ? 'is-active' : '',
                  o.disabled ? 'is-disabled' : '',
                ].filter(Boolean).join(' ');
                const optionEl = (
                  <div
                    key={o.value}
                    id={optId(o.value)}
                    role="option"
                    aria-label={name}
                    aria-selected={o.value === value}
                    aria-disabled={o.disabled || undefined}
                    title={o.disabled ? o.disabledReason : undefined}
                    className={cls}
                    onMouseEnter={() => { if (!o.disabled) setActive(o.value); }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(o)}
                  >
                    <span className="wc-gselect__label">{o.label}</span>
                    {o.tag && <span className="wc-gselect__tag">{o.tag}</span>}
                    {o.meta && <span className="wc-gselect__meta">{o.meta}</span>}
                    {o.detail && <span className="wc-gselect__detail">{o.detail}</span>}
                  </div>
                );
                if (!(onRemove && o.removable)) return optionEl;
                return (
                  <div key={o.value} className="wc-gselect__row">
                    {optionEl}
                    <button
                      type="button"
                      className="wc-gselect__remove"
                      tabIndex={-1}
                      aria-label={removeLabel ? removeLabel(o) : `${o.label} 지우기`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onRemove(o)}
                    >
                      <X size={12} aria-hidden />
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {footer && <div className="wc-gselect__foot">{footer(() => close(true))}</div>}
    </div>
  );

  return (
    <div className={['wc-gselect', open ? 'wc-gselect--open' : '', className ?? ''].filter(Boolean).join(' ')}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={activeId}
        aria-label={ariaLabel}
        disabled={disabled}
        className="wc-gselect__trigger"
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        onKeyDown={onTriggerKeyDown}
      >
        {renderValue
          ? <span className="wc-gselect__value">{renderValue(selected)}</span>
          : <span className={selected ? 'wc-gselect__value' : 'wc-gselect__placeholder'}>{selected?.label ?? placeholder}</span>}
        {summary && <small className="wc-gselect__summary">{summary}</small>}
        <ChevronDown className="wc-gselect__chevron" aria-hidden />
      </button>
      {open && createPortal(popover, document.body)}
    </div>
  );
}
